import React, { useCallback } from 'react';
import { Tbody, Tr, Td } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { DocumentDetailFilterResponse } from '@mercator-gcp/types/elastic_search_service/documents/DocumentDetailFilterResponse';
import { useSearchParams } from 'react-router-dom';
import { EmptyField } from 'components/elements/EmptyField';
import { PermitChip } from 'components/elements/PermitChip';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import CompaniesCell from 'components/widgets/DocumentSearch/Table/cells/Companies';
import DescriptionCell from 'components/widgets/DocumentSearch/Table/cells/Description';
import ViewProjectButton from 'components/widgets/DocumentSearch/Table/cells/ViewProjectButton';
import { trackViewStageDetails } from 'utils/analytics';
import {
  DocumentSearchPageViewSections,
  ProjectGroupTypes,
} from 'utils/analytics/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { formatDate } from 'utils/date';
import { formatPrice } from 'utils/format';
import { titleCase } from 'utils/misc';
import { renameDocuments } from 'utils/renameDocuments';
import type { TColumnProps } from 'components/elements/LeadsTable/types';

const addressText = {
  textTransform: 'capitalize',
};

const rowStyle = {
  backgroundColor: 'transparent',
  '&:hover': {
    cursor: 'pointer',
    td: {
      background: Colors.ArmyGreen100,
    },
  },
  td: {
    backgroundColor: Colors.White,
  },
};

type Props = TColumnProps & {
  items?: DocumentDetailFilterResponse['results'];
  isLoading: boolean;
};

const TableBody = ({ items, isLoading }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  if (!items) {
    return null;
  }
  const onRowClick = useCallback(
    (
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
      documentTypeCode: string,
      documentId: string,
    ) => {
      // Put the modal opening for the Document inside a Callback because
      // we want to make sure we are picking up the latest page number to append URL query parameters.
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set(QUERY_PARAM.documentType, documentTypeCode);
      newSearchParams.set(QUERY_PARAM.documentId, documentId);

      setSearchParams(newSearchParams);

      trackViewStageDetails({
        groupId: String(0),
        groupType: ProjectGroupTypes.UNCATEGORIZED,
        stageId: documentId,
        stageType: documentTypeCode,
        clickLocation: DocumentSearchPageViewSections.DOCUMENT_TABLE,
        clickText: undefined,
      });
    },
    [searchParams],
  );

  if (isLoading) {
    return (
      <Tbody>
        <Tr>
          <Td colSpan={100}>
            <Spinner />
          </Td>
        </Tr>
      </Tbody>
    );
  }

  return (
    <Tbody>
      {items?.map((document) => {
        const renamedDocument = renameDocuments(document.document_type_name);

        return (
          <Tr
            id={`document-search-body-table-row-${document.id}`}
            key={`document-search-body-table-row-${document.id}`}
            onClick={(e) => onRowClick(e, document.document_type_code, document.id)}
            sx={rowStyle}
          >
            <Td>{formatDate(document.earliest_activity_date)}</Td>
            <Td>{formatDate(document.latest_activity_date)}</Td>
            <Td>
              <PermitChip type={renamedDocument}>{renamedDocument}</PermitChip>
              {document?.latest_status && (
                <Text.Small as="div">{titleCase(document.latest_status)}</Text.Small>
              )}
            </Td>
            <Td>
              <FlexColumn>
                <Text.BodyBold sx={addressText}>{document.parcel_address}</Text.BodyBold>
                <Text.Body
                  color={Colors.ArmyGreen700}
                >{`${document.city_name}, ${document.state}`}</Text.Body>
              </FlexColumn>
            </Td>
            <Td>
              <DescriptionCell name={document.name} description={document.description} />
            </Td>
            <Td>
              {document.estimated_project_cost ? (
                <Text.Body>{formatPrice(document.estimated_project_cost)}</Text.Body>
              ) : (
                <EmptyField />
              )}
            </Td>
            <Td>
              <CompaniesCell companies={document.companies} />
            </Td>

            <Td>
              <ViewProjectButton
                documentTypeCode={document.document_type_code}
                documentId={document.id}
                projectSlugs={document.project_slugs}
              />
            </Td>
          </Tr>
        );
      })}
    </Tbody>
  );
};

export default TableBody;
